import React, { useState, useRef, useEffect, Fragment, ReactElement } from 'react'
import Slider from 'react-slick'
import { useGetFeaturedPropertiesQuery } from '@apis/middlewareTokkoApi/properties'
import { OperationTokko, Property, PropertyMediacore, RealEstateDataPageComponentData } from 'types'
import { Col, Container, Row } from 'react-bootstrap'
import useQueryProperties from '@hooks/useQueryProperties'
import { Link } from 'gatsby'
import { excludeIncludeProperties, getOperations } from '@helpers/helper.properties'

// Desktop config for slider
const settingsDesktop = {
  dots: false,
  infinite: false,
  speed: 500,
  items: 3,
  margin: 5,
  slidesToShow: 3,
  centerMode: false,
  centerPadding: `10px`,
  slidesToScroll: 1,
  autoplay: false,
  autoplaySpeed: 3000,
  arrows: false,
  pauseOnHover: false,
  responsive: [
    {
      breakpoint: 1280,
      settings: {
        items: 3,
        slidesToShow: 3,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 992,
      settings: {
        items: 3,
        slidesToShow: 3,
        slidesToScroll: 2,
      },
    },
    {
      breakpoint: 768,
      settings: {
        items: 1,
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
}

// Mobile config for slider
const settingsMobile = {
  dots: false,
  infinite: true,
  speed: 500,
  fade: false,
  slidesToShow: 1,
  centerMode: true,
  slidesToScroll: 1,
  responsive: [
    {
      breakpoint: 480,
      settings: {
        centerPadding: `30px`,
      },
    },
  ],
}

const SummaryProps_2 = (props: RealEstateDataPageComponentData) => {
  const [isMounted, setIsMounted] = useState(false)
  const {
    title,
    global_data,
    hidden,
    includeTag,
    excludeTag,
    titleSecondary,
    button,
    locationsAdv,
  } = props
  const { divisionPath } = global_data
  const propCard = global_data.content?.cardsProperties?.type_id ?? '1'
  const sliderRef = useRef<any>()
  const tokkoKey = global_data.pageConfig.keys.tokko

  const [properties_filtered, setProperties] = useState<Property[]>()

  const { data: featuredPropertiesData, isLoading: isLoadingProperties } = tokkoKey
    ? useGetFeaturedPropertiesQuery({ API_KEY: tokkoKey })
    : useQueryProperties({
        //@ts-ignore
        filters: {
          location: [],
          type: [],
          operation: '',
          environments: '',
          bedrooms: '',
          age: '',
          address: '',
          producer_id: '',
          price: {},
          environments_types: [],
          generals: [],
          services: [],
          specials: [],
        },
        tokkoKey,
      })

  const Card = require(`@components/cardsProperties/${propCard}`)?.default

  const [filter, setFilter] = useState(locationsAdv ? '' : 'Venta')
  const buttons = [
    {
      name: 'Venta',
      data: 'Venta',
      value: 0,
    },
    {
      name: 'Alquiler',
      data: 'Alquiler',
      value: 1,
    },
    {
      name: 'Temporario',
      data: 'Alquiler temporario',
      value: 2,
    },
  ]

  // Check if operation type has same button value, return length
  const checkOperations = (properties: Property[], value: string): number => {
    let arrProp: Property[] = []
    if (properties && properties?.length > 0) {
      properties?.forEach((property: Property) => {
        property?.operations?.forEach((operation: OperationTokko) => {
          if (operation?.operation_type === value) arrProp.push(property)
        })
      })
    }
    return arrProp?.length
  }

  // Check props filtered length, if it has 1 or 2 elements concat elements (fix for Slider component, min 3 props values)
  const checkFillPops = (props: Property[]): Property[] => {
    // if (props?.length === 2) {
    //   return props?.concat(props)
    // } else if (props?.length === 1) {
    //   return props?.concat(props)?.concat(props)
    // }
    return props
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (featuredPropertiesData?.objects) {
      setProperties(
        excludeIncludeProperties(featuredPropertiesData?.objects, includeTag!, excludeTag!),
      )
    }
  }, [featuredPropertiesData])

  function getTypes(properties: Property[]) {
    let arrTypes: string[] = []
    if (properties && properties?.length > 0) {
      properties?.forEach((property: Property) => {
        if (!arrTypes.find((type) => type === property?.type.name))
          arrTypes.push(property.type.name)
      })
    }
    return arrTypes
  }

  // Desktop Slider element
  const DesktopLoaderSliderEl = (): ReactElement => {
    return isLoadingProperties ? (
      <div className="row">
        {/* Slider with keleton */}
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className="col-lg-4 px-lg-4 mb-lg-5 mb-4"
          >
            <Fragment>
              <Card
                key={index}
                skeleton={true}
                global_data={global_data}
              />
            </Fragment>
          </div>
        ))}
      </div>
    ) : isMounted ? (
      <>
        {/* Slider with data props */}
        <Slider
          ref={sliderRef}
          {...settingsDesktop}
          lazyLoad="ondemand"
          className={'overflow-hidden ' + isLoadingProperties ? 'w-100' : ''}
        >
          {properties_filtered?.length &&
            checkFillPops(
              properties_filtered?.filter((property: Property) =>
                locationsAdv
                  ? property.type.name === filter || filter === ''
                  : property.operations.find((operation) => operation.operation_type === filter),
              ),
            ).map((prop: Property, index: number) => (
              <Fragment key={index}>
                <Card
                  key={index}
                  basic={true}
                  property={prop}
                  customOperation={
                    filter.toLowerCase().includes('temporario')
                      ? 'alquiler temporario'
                      : locationsAdv
                      ? getOperations(prop)?.at(0)
                      : filter?.toLowerCase()
                  }
                  global_data={global_data}
                />
              </Fragment>
            ))}
        </Slider>
        {ArrowsEl(sliderRef)}
      </>
    ) : (
      <div></div>
    )
  }

  // Mobile Slider element
  const MobileLoaderSliderEl = (): ReactElement => {
    return isLoadingProperties ? (
      <>
        {/* Slider with keleton */}
        <Slider
          ref={sliderRef}
          {...settingsMobile}
          lazyLoad="ondemand"
          className="slider-mobile"
        >
          {[1, 2, 3].map((index) => (
            <Fragment key={index}>
              <Card
                key={index}
                skeleton={true}
                global_data={global_data}
              />
            </Fragment>
          ))}
        </Slider>
      </>
    ) : (
      <>
        {/* Slider with data props */}
        <Slider
          ref={sliderRef}
          {...settingsMobile}
          lazyLoad="ondemand"
          className="slider-mobile"
        >
          {properties_filtered &&
            checkFillPops(
              properties_filtered?.filter((property: Property) =>
                locationsAdv
                  ? property.type.name === filter || filter === ''
                  : property.operations.find((operation) => operation.operation_type === filter),
              ),
            )?.map((prop: Property, index: number) => (
              <Fragment key={index}>
                <Card
                  key={index}
                  basic={true}
                  property={prop}
                  global_data={global_data}
                  customOperation={
                    filter.toLowerCase().includes('temporario')
                      ? 'alquiler temporario'
                      : locationsAdv
                      ? getOperations(prop)?.at(0)?.toLowerCase()
                      : filter?.toLowerCase()
                  }
                />
              </Fragment>
            ))}
        </Slider>
        {ArrowsEl(sliderRef)}
      </>
    )
  }

  // Arrow element
  const ArrowsEl = (sliderRef: React.MutableRefObject<any>): ReactElement => {
    return !hidden ? (
      <div className="arrows-owl d-none d-lg-flex">
        {/* arrows mobile */}
        <div
          className="d-lg-none arrow"
          onClick={() => sliderRef.current.slickPrev()}
        >
          {' '}
          <i className="isArrowFeatured"></i> <i className="isArrowFeatured"></i>
        </div>
        <div
          className="d-lg-none arrow reverse"
          onClick={() => sliderRef.current.slickNext()}
        >
          {' '}
          <i className="isArrowFeatured"></i> <i className="isArrowFeatured"></i>{' '}
        </div>
        {/* arrows desktop */}
        <i
          className="d-none d-lg-block icon-arrow-left isArrowFeatured"
          onClick={() => sliderRef.current.slickPrev()}
        ></i>
        <i
          className="d-none d-lg-block icon-arrow isArrowFeatured"
          onClick={() => sliderRef.current.slickNext()}
        ></i>
      </div>
    ) : (
      <></>
    )
  }

  return (
    <section
      className={
        'b p-y-config' +
        (!isLoadingProperties &&
        (featuredPropertiesData?.objects?.length === 0 || featuredPropertiesData === undefined)
          ? ' d-none'
          : '')
      }
      id="featured-props"
    >
      {/* Title */}
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2
              className={
                'section-title pb-5 text-center text-uppercase ' +
                (titleSecondary ? 'text-primary' : '')
              }
            >
              {title}
            </h2>
          </Col>
        </Row>
      </Container>
      {/* Filter Buttons */}
      <Container
        fluid
        className="my-1 filter-wrap mb-4"
      >
        <div className="d-flex flex-row flex-wrap flex-lg-row align-items-center align-items-lg-start justify-content-center">
          {locationsAdv
            ? getTypes(featuredPropertiesData?.objects).map((type, index) => (
                <button
                  key={index}
                  className={
                    'btn btn-primary border mb-2 mb-lg-0 fst-italic ' +
                    (filter === type ? ' active ' : '')
                  }
                  onClick={() => setFilter(type)}
                >
                  {type === 'Terreno' ? 'Terreno o Lote' : type}
                </button>
              ))
            : buttons.map((button, index) => (
                <button
                  key={index}
                  className={
                    'btn btn-primary border mb-2 mb-lg-0 fst-italic ' +
                    (checkOperations(featuredPropertiesData?.objects, button.data) === 0
                      ? ' d-none '
                      : '') +
                    (filter === button.data ? ' active ' : '')
                  }
                  onClick={() => setFilter(button.data)}
                >
                  {button.name === 'Alquiler temp.' && screen.width < 992
                    ? 'Alquiler temporario'
                    : button.name}
                </button>
              ))}
        </div>
      </Container>
      {/* Properties Slider */}
      <>
        {/* Desktop loader and slider */}
        <Container fluid>
          <Row>
            <Container fluid>
              <div className="props d-flex align-items-center justify-content-center d-none d-lg-block">
                {DesktopLoaderSliderEl()}
              </div>
            </Container>
          </Row>
        </Container>
        {/* Mobile loader and slider */}
        <div className="d-block d-lg-none position-relative">{MobileLoaderSliderEl()}</div>
      </>
      {/* Bottom bnt */}
      {button?.link && (
        <Container
          fluid
          className="mt-4 mt-lg-0"
        >
          <Row>
            <Col
              xs={12}
              className="d-flex justify-content-center"
            >
              <Link
                to={button?.link}
                className="btn btn-primary more-props"
              >
                {button?.value}
              </Link>
            </Col>
          </Row>
        </Container>
      )}
    </section>
  )
}

export default SummaryProps_2
